import { useCallback, useMemo, useState } from 'react'

import { Box, Icon, Text } from '@factorialco/deprecated-design-system'
import {
  CompetenciesCompetenciesFinderInitialComboboxDocument,
  CompetenciesCompetenciesSearchFactorialCompetenciesDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import {
  SelectedCompetency,
  sortSelectedCompetencies,
  toSelectedCompetency,
} from 'modules/competencies/hooks/useCompetencyFinder'

import BaseCombobox from './BaseCombobox'
import { SkeletonOptions } from './SkeletonOption'

import './index.styles.css'

function selectionByType(selected: SelectedCompetency[]) {
  const initial: { all: string[]; company: string[]; companyInt: string[]; factorial: string[] } = {
    all: [],
    company: [],
    companyInt: [],
    factorial: [],
  }
  return sortSelectedCompetencies(selected).reduce((acc, item) => {
    const identifier = item.identifier

    acc.all.push(identifier)

    if (item.type === 'company_competency') {
      acc.company.push(identifier)
      const companyId = identifier
      if (companyId) {
        acc.companyInt.push(companyId)
      }
      if (item.factorialCompetencyUuid) {
        acc.factorial.push(item.factorialCompetencyUuid)
      }
    } else if (item.type === 'factorial_competency') {
      acc.factorial.push(identifier)
    }

    return acc
  }, initial)
}

type Tooltip =
  | {
      title?: string
      description: string
    }
  | null
  | undefined

function tooltip(competency: SelectedCompetency): Tooltip {
  const description = competency.ui?.tooltip?.description || competency.description || ''
  if (!description.trim()) {
    return
  }

  return {
    title: competency.ui?.tooltip?.title,
    description,
  }
}

type Props = {
  useFactorialCompetencies: boolean
  selectedCompetencies: SelectedCompetency[]
  canCreate: boolean
  onAdd: (competency: SelectedCompetency) => void
  onRemove: (competency: SelectedCompetency) => void
  onCreateNew: (name?: string) => void
}

export const Combobox = ({
  useFactorialCompetencies,
  selectedCompetencies = [],
  canCreate,
  onAdd,
  onRemove,
  onCreateNew,
}: Props) => {
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)
  const { company } = useCurrentSession()

  const { externalExclusions, selectedIdentifiers } = useMemo(() => {
    const sortedSelected = [...selectedCompetencies].sort((a, b) => a.name.localeCompare(b.name))
    const externalCompetencies = sortedSelected.filter((c) => c.source !== 'combobox')

    return {
      externalExclusions: selectionByType(externalCompetencies),
      selectedIdentifiers: selectionByType(selectedCompetencies),
    }
  }, [selectedCompetencies])

  const { data: companySuggestions, loading: loadingCompanySuggestions } = useQuery(
    CompetenciesCompetenciesFinderInitialComboboxDocument,
    {
      variables: { search: search, excludeIds: externalExclusions.companyInt },
    }
  )

  const { data: factorialSuggestions, loading: loadingFactorialSuggestions } = useQuery(
    CompetenciesCompetenciesSearchFactorialCompetenciesDocument,
    {
      variables: {
        search: search,
        companyId: company.id,
        excludeUuids: externalExclusions.factorial,
      },
      skip: !useFactorialCompetencies,
    }
  )

  const { optionsArray, optionsLookup } = useMemo(() => {
    const factorialLimitedSuggestions =
      factorialSuggestions?.competencies?.factorialCompetenciesConnection?.nodes || []
    const factorialOptions = factorialLimitedSuggestions
      .filter((suggestion) => !selectedIdentifiers.factorial.includes(suggestion.uuid))
      .map((suggestion) => toSelectedCompetency(suggestion, 'combobox'))

    const companyData = companySuggestions?.competencies?.competenciesConnection?.edges || []
    const companyOptions = companyData
      .filter(({ node }) => !selectedIdentifiers.companyInt.includes(node.id))
      .map(({ node }) => toSelectedCompetency(node, 'combobox'))

    const allOptions = sortSelectedCompetencies([
      ...selectedCompetencies,
      ...companyOptions,
      ...factorialOptions,
    ])
    const lookup = new Map(allOptions.map((option) => [option.identifier, option]))
    return {
      optionsArray: allOptions,
      optionsLookup: lookup,
    }
  }, [
    selectedCompetencies,
    companySuggestions,
    factorialSuggestions,
    selectedIdentifiers.factorial,
    selectedIdentifiers.companyInt,
  ])

  const handleOnChange = (ids: string[] | string | null) => {
    if (!ids) {
      return
    }

    const newIdentifiers = Array.isArray(ids) ? ids : [ids]
    const oldSet = new Set(selectedIdentifiers.all)
    const newSet = new Set(newIdentifiers)
    const added = newIdentifiers.filter((id) => !oldSet.has(id))
    const removed = selectedIdentifiers.all.filter((id) => !newSet.has(id))

    added.forEach((identifier) => {
      const competency = optionsLookup.get(identifier)
      if (competency) {
        onAdd(competency)
      }
    })

    removed.forEach((identifier) => {
      const competency = optionsLookup.get(identifier)
      if (competency) {
        onRemove(competency)
      }
    })
  }

  const handleClickNew = (name?: string) => {
    setOpen(false)
    onCreateNew(name)
  }

  const creationComponent = canCreate
    ? (newName: string) => (
        <Box
          className={'Combobox_createOption'}
          alignItems="center"
          flexDirection="row"
          paddingX="s16"
          paddingY="s12"
          gap="s4"
          role="button"
          cursor="pointer"
          onClick={() => handleClickNew(newName)}
        >
          <Icon.Medium icon="add" />
          <Text.Normal size="200" weight="medium">
            {newName
              ? i18n.t('competencies_v3.competencies_finder.create_form.create_new', {
                  competencyName: newName,
                })
              : i18n.t('competencies_v3.competencies_finder.create_form.create_new_empty')}
          </Text.Normal>
        </Box>
      )
    : () => <></>

  const loadingComponent = () => <SkeletonOptions identifier="loading-search" quantity={3} />
  const handleInputChange = useCallback((input: string) => {
    setSearch(input)
  }, [])
  return (
    <BaseCombobox.Popover
      isOpen={open}
      onToggle={() => setOpen(!open)}
      label={i18n.t('competencies_v3.competencies_finder.selector.label')}
      aria-label={i18n.t('competencies_v3.competencies_finder.selector.label')}
      blankLabel={i18n.t('competencies_v3.competencies_finder.selector.no_found')}
      placeholder={i18n.t('competencies_v3.competencies_finder.selector.placeholder')}
      isMultiSelect
      value={selectedIdentifiers.all}
      onChange={(items) => handleOnChange(items)}
      creationComponent={creationComponent}
      debouncedMinimumCharacters={3}
      onDebouncedSearch={handleInputChange}
      loadingSearch={loadingCompanySuggestions || loadingFactorialSuggestions}
      loadingSearchComponent={loadingComponent}
      missingLabel={i18n.t('common.missing_field')}
      selectValue={i18n.t('common.select')}
      defaultSearchPlaceholder={i18n.t('competencies_v3.competencies_finder.selector.placeholder')}
    >
      {optionsArray.map((competency) => (
        <BaseCombobox.Option
          key={competency.identifier}
          value={competency.identifier}
          label={competency.name}
          searchValue={competency.name}
          isDisabled={competency.readonly}
          icon={competency.ui?.icon}
          tooltip={tooltip(competency)}
          allowFilter={competency.type !== 'factorial_competency'}
        >
          <Box flexDirection="column">
            <Text.Normal
              size="300"
              weight="medium"
              color={competency.readonly ? 'grey600' : 'grey1000'}
            >
              {competency.name}
            </Text.Normal>
            <Text.Normal
              size="200"
              weight="normal"
              color={competency.readonly ? 'grey600' : 'grey800'}
            >
              {competency.description}
            </Text.Normal>
          </Box>
        </BaseCombobox.Option>
      ))}
    </BaseCombobox.Popover>
  )
}
